import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hasUnseenVersion } from '../services/versionService';
import { listDocs } from '../services/docsService';

interface UseWhatsNewResult {
  open: boolean;
  version: string | null;          // = the latest release-notes slug (e.g., r2026.13)
  dismiss: () => void;
  preview: () => void;
}

/**
 * "What's New" trigger logic.
 *
 * The modal shows the latest release-notes doc. Source of truth is the
 * docs catalog (NOT /api/version) — VERSION drifts from release-notes
 * filenames during patch addenda (e.g., backend/VERSION may say
 * `r2026.12.4` while the latest release notes file is `r2026.13.md`).
 *
 * Catalog lists Release Notes group sorted filename_descending, so
 * docs[0] of that group is the latest.
 */
export function useWhatsNew(): UseWhatsNewResult {
  const [open, setOpen] = useState(false);

  // Same ['docs-list'] query key Documentation page uses — cache shared.
  const { data: catalog } = useQuery(['docs-list'], listDocs, {
    staleTime: 300000,
  });

  const latestSlug = useMemo(() => {
    if (!catalog) return null;
    const releaseDocs = catalog.docs.filter((d) => d.group === 'Release Notes');
    return releaseDocs[0]?.slug ?? null;
  }, [catalog]);

  useEffect(() => {
    if (latestSlug && hasUnseenVersion(latestSlug)) {
      setOpen(true);
    }
  }, [latestSlug]);

  const dismiss = () => setOpen(false);
  const preview = () => setOpen(true);
  // markVersionSeen is intentionally NOT called here. The modal body's
  // useQuery.onSuccess calls it once the doc actually loads — so the
  // header badge clears once the user has seen the content, not just
  // because the modal opened.

  return {
    open,
    version: latestSlug,
    dismiss,
    preview,
  };
}
